<template>
  <v-overlay
    :model-value="loading"
    class="align-center justify-center loading-overlay"
    :scrim="scrimColor"
    :persistent="persistent"
    :close-on-content-click="false"
    transition="fade-transition"
  >
    <div
      class="d-flex flex-column align-center loading-content"
      :style="{ 'max-width': `${maxWidth}px` }"
    >
      <div class="logo-container mb-4">
        <v-img
          :src="logo"
          :width="logoSize"
          :height="logoSize"
          class="rotate"
          :class="{ 'pulse': !rotate }"
          :alt="logoAltText"
        />
      </div>

      <v-card-text
        class="text-h6 font-weight-bold text-center loading-text"
        :style="{ color: computedTextColor }"
      >
        {{ message }}
      </v-card-text>

      <div class="d-flex justify-center mt-4 progress-indicators">
        <template v-if="progressType === 'circular'">
          <v-progress-circular
            v-for="i in progressCount"
            :key="`circular-${i}`"
            indeterminate
            :color="progressColor"
            :size="progressSize"
            :width="progressWidth"
            class="mx-2"
            :style="{ 'animation-delay': `${i * 0.15}s` }"
          />
        </template>

        <v-progress-linear
          v-else
          indeterminate
          :color="progressColor"
          height="6"
          class="mt-4"
          :style="{ width: `${progressLinearWidth}px` }"
        />
      </div>

      <slot name="additional-content"></slot>
    </div>
  </v-overlay>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  persistent: {
    type: Boolean,
    default: true
  },
  scrimColor: {
    type: String,
    default: 'rgba(15, 15, 15, 0.8)'
  },
  logo: {
    type: String,
    default: 'https://res.cloudinary.com/dzrldiajp/image/upload/w_150/v1765834427/vbzyvyi1lif0ub6dk9x2.png'
  },
  logoSize: {
    type: [Number, String],
    default: 150
  },
  logoAltText: {
    type: String,
    default: 'Loading indicator'
  },
  rotateLogo: {
    type: Boolean,
    default: true
  },
  message: {
    type: String,
    default: 'Please Wait...'
  },
  textColor: {
    type: String,
    default: ''
  },
  progressType: {
    type: String,
    default: 'circular', // 'circular' or 'linear'
    validator: (value) => ['circular', 'linear'].includes(value)
  },
  progressCount: {
    type: Number,
    default: 3,
    validator: (value) => value > 0 && value <= 5
  },
  progressColor: {
    type: String,
    default: 'primary'
  },
  progressSize: {
    type: Number,
    default: 24
  },
  progressWidth: {
    type: Number,
    default: 3
  },
  progressLinearWidth: {
    type: Number,
    default: 200
  },
  maxWidth: {
    type: Number,
    default: 300
  }
});

const store = useStore();
const loading = computed(() => store.getters.getLoadingState);
const computedTextColor = computed(() =>
  props.textColor || 'rgb(var(--v-theme-on-surface))'
);
</script>

<style lang="scss" scoped>
.loading-overlay {
  z-index: 9999;

  .loading-content {
    padding: 2rem;
    border-radius: 12px;
    background: rgba(var(--v-theme-surface-rgb), 0.95);
    border: 1px solid rgb(var(--v-theme-border-color));
    backdrop-filter: blur(8px);
    box-shadow: 0 16px 45px rgba(var(--v-theme-on-surface-rgb), 0.18);

    .loading-text {
      letter-spacing: 1px;
      text-shadow: 0 1px 2px rgba(var(--v-theme-on-surface-rgb), 0.25);
    }

    .progress-indicators {
      .v-progress-circular {
        opacity: 0;
        animation: fadeInOut 1.5s infinite ease-in-out;

        @for $i from 1 through 5 {
          &:nth-child(#{$i}) {
            animation-delay: $i * 0.15s;
          }
        }
      }
    }
  }
}

.rotate {
  animation: rotation 1.5s infinite linear;
}

.pulse {
  animation: pulse 1.5s infinite ease-in-out;
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg);
  }
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.8;
  }
}

@keyframes fadeInOut {
  0%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
